import { useMemo, type ComponentProps, type ComponentType } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { NavMain } from './components/NavMain';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { NavUser } from './components/NavUser';
import { NavSecondary } from './components/NavSecondary';
import { useUserStore } from '@/stores/userStore';
import { getHasPermissionRoutes, routes } from '@/router/routes';
import type { HasMetaRouteWithFullPath } from '@/router';
import { mapRoutesWithFullPath } from '@/router/path';
import { LogoElement } from './components/LogoElement';

// 主导航菜单项类型
export interface NavMainItem {
  title: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
  isActive?: boolean;
  items?: {
    title: string;
    url: string;
  }[];
}

// 次级导航菜单项类型
export interface NavSecondaryItem {
  title: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
  meta: string;
}

export interface NavSecondaryMessageItem {
  title: string;
  icon: ComponentType<{ className?: string }>;
}

const navSecondary = [] as NavSecondaryItem[];

export default function AppSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useUserStore();
  const navMainRoutes = useMemo(() => {
    const permissionRoutes = getHasPermissionRoutes(
      userInfo?.permissionCodes || [],
      routes[0].children || [],
      (route) => route.permCode !== undefined && route.path !== '' && !!route.meta
    );
    return mapRoutesWithFullPath(permissionRoutes) as HasMetaRouteWithFullPath[];
  }, [userInfo?.permissionCodes]);

  return (
    <Sidebar collapsible="compact" {...props}>
      {/* 侧边栏头部 */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:px-2.5 pt-2 pb-1 h-9 text-nowrap"
            >
              <LogoElement />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* 侧边栏内容 */}
      <SidebarContent>
        <NavMain routes={navMainRoutes} location={location} navigate={navigate} />
        <NavSecondary items={navSecondary} className="mt-auto" />
      </SidebarContent>

      {/* 侧边栏底部 */}
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
